import { AppBar, Grid, Toolbar, useTheme } from "@mui/material";
import {
	BackdropCircularProgress,
	EmptyList,
	EnhancedSnackbar,
} from "@websential/cosmic-v20";
import {
	EMPLOYEE_LIST_SCREEN_SLUG,
	INITIAL_PAGE_SIZE,
	INITIAL_SNACKBAR_DATA,
	ROWS_PER_PAGE_PER_OPTIONS,
} from "../../../common/constants";
import { FormProvider, useForm } from "react-hook-form";
import { GET_QUERY_KEY, QUERY_OPTIONS } from "../../../common/constants";
import { IResponse, IServerError } from "../../../models/common";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {

	formatDate,
	getDDLLabelByValue,
	sortDDLByLabel,
	useNavigateToLogout,
	useRemoveQueriesOnUnmount
} from "../../../common/Common";
import {
	setSelectedActivityLogActions,
	setSelectedStickyNavBar,
} from "../../../store/actions";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { AuthService } from "../../../services";
import { CRARamittanceService } from "../../../services/CRARamittanceService";
import { WSIBReportDataGridWithColumnToggled } from "./WSIBReportDataGridWithColumnToggled";
import { WSIBReportHeader } from "./WSIBReportHeader"
import { CompanyTD1FormService } from "../../../services/CompanyTD1FormService";
import { DateRange } from "@mui/icons-material";
import { EmployeeService } from "../../../services/EmployeeService";
import FileSaver from "file-saver";
import { IAppState } from "../../../store/reducers";
import { IDropDownListResponse } from "../../../models/common/IDropDownListResponse";
import { IGetListRequest } from "../../../models";
import { LookupFieldService } from "../../../services/LookupFieldService";
import { ReportsService } from "../../../services/ReportsService";
import { endOfDay } from "date-fns";
import { setSelectedDrawerOptionsMenu } from "../../../store/actions/drawerMenuOptionsActions";
import { useNavigate } from "react-router-dom";
import { DepartmentsService } from "../../../services/DepartmentsService";
import { groupBy } from "lodash";
import { YearToDateService } from "../../../services/YearToDateService";

export interface IWSIBReportSetupPage { }
const WSIB_REPORT_QUERY_KEY = "wsib-report";
const EMPLOYEE_DDL_DATA_QUERY_KEY = "employee-ddl-data-record";

const useWSIBReport = (
	runGetListAPICall: boolean,
	clientId: string,
	taxYear: number,
	groupBy: string,
	department: string,
	startDate: string | null,
	endDate: string | null,
	params: IGetListRequest,
	navigateToLogout: (path: any) => void
) =>
	useQuery({
		queryKey: [
			WSIB_REPORT_QUERY_KEY,
			runGetListAPICall,
			clientId,
			taxYear,
			groupBy,
			department,
			startDate,
			endDate,
			params,

		],
		queryFn: () =>
			ReportsService.getWSIBReport(
				runGetListAPICall,
				clientId,
				taxYear,
				groupBy,
				department,
				startDate,
				endDate,
				params,
				navigateToLogout
			),
		refetchOnWindowFocus: false,
	});

const useDepartmentsDDL = (
	clientId: string,
) =>
	useQuery({
		queryKey: ["departments-list"],
		queryFn: (data) => {

			return DepartmentsService.getGetDepartmentsDDL(
				clientId,
			);
		},
		refetchOnWindowFocus: false,
	});

const useGetYearsDDL = (clientId: string, taxYear: number) =>
	useQuery({
		queryKey: ["year-to-date-ddl", clientId, taxYear],
		queryFn: (data) => {
			return YearToDateService.getYearsDDL(clientId, taxYear);
		},
		refetchOnWindowFocus: false,
	});


export const WSIBReport: React.FC<IWSIBReportSetupPage> = () => {
	const theme = useTheme();
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const clientIdStore = useSelector((state: IAppState) => state.client.id);
	const clientName = useSelector((state: IAppState) => state.client.clientName);
	const clientId = useSelector((state: IAppState) => state.client.id);
	const taxYear = useSelector((state: IAppState) => state.taxYear.year);
	const drawerMenuOptions = useSelector(
		(state: IAppState) => state.drawerMenu.menu
	);
	const navigateToLogout = useNavigateToLogout();

	// for delete row Purpose
	const queryClient = useQueryClient();
	const [snackbarData, setSnackbarData] = useState(INITIAL_SNACKBAR_DATA);
	const [isLoading, setIsLoading] = useState(false);
	const [wsibReportData, setWSIBReportData] =
		useState<any>(undefined);
	const [runGetListAPICall, setRunGetListAPICall] =
		useState<any>(false);


	const formMethods = useForm<any>({
		// resolver: yupResolver(ValidationSchema),
		mode: "onBlur",
		reValidateMode: "onChange",
	});

	const [queryParams, setQueryParams] = useState<IGetListRequest>({
		search_query: "",
		order_by: "name",
		order_direction: "asc",
		page: 1,
		size: 25,
	});

	const { isLoading: isDepartmentLoadingDDL, data: departmentDDL } = useDepartmentsDDL(clientId);
	const { isLoading: isYearTODateDDLLoading, data: yearsDDLItems } = useGetYearsDDL(clientId, taxYear);

	const selectedYear = formMethods.watch("selectedYear");
	const groupBy = formMethods.getValues("groupBy")
	const department = formMethods.getValues("department");
	const startDate = formMethods.getValues("startDate");
	const endDate = formMethods.getValues("endDate");

	const departmentsDDLItems = useMemo(() => {
		if (departmentDDL) {
			if (
				departmentDDL &&
				Array.isArray(departmentDDL)
			) {
				let resources: IDropDownListResponse[] =
					sortDDLByLabel(departmentDDL).map(
						(resourceItem: IDropDownListResponse) => ({
							// id: resourceItem.id,
							label: resourceItem.label,
							value: resourceItem.value,
						})
					);

				resources.unshift({
					label: "All",
					value: "all",
				});

				return resources;
			}
			return [];
		}
	}, [departmentDDL]);

	const { isPending, data } = useWSIBReport(
		runGetListAPICall,
		clientId,
		selectedYear && getDDLLabelByValue(yearsDDLItems, selectedYear),
		groupBy,
		department,
		formatDate(startDate),
		formatDate(endDate),
		queryParams,
		navigateToLogout
	);
	const WSIBReportList: any | null = data;


	useEffect(() => {
		if (WSIBReportList) {
			setWSIBReportData(WSIBReportList);
		}
	}, [WSIBReportList]);


	const wsibReportDataAPICall = (
		selectedYear: any,
		groupBy: string,
		department: string,
		startDate: string | null,
		endDate: string | null,
	) => {
		const fetchData = async () => {
			setIsLoading(true);
			try {
				const response: any = await ReportsService.getWSIBReport(
					true,
					clientIdStore,
					 getDDLLabelByValue(yearsDDLItems, selectedYear),
					groupBy,
					department,
					formatDate(startDate),
					formatDate(endDate),
					queryParams,
					navigateToLogout
				);
				if (response) {
					setWSIBReportData(response);
				}
			} catch {
			} finally {
				setIsLoading(false);
			}
		};
		fetchData();
	};

	const onSubmit = (values: any) => {
		wsibReportDataAPICall(
			values.selectedYear,
			values.groupBy,
			values.department,
			values.startDate,
			values.endDate
		);
	};

	const [zeroEmployee, setzeroEmployee] = useState(true);

	const [submitAddRemoveColumnsHandler, setSubmitAddRemoveColumnsHandler] =
		useState(undefined);
	const [onColumnReorder, setOnColumnReorder] = useState(undefined);

	const onRequestParamsChange = useCallback((requestParams: any) => {
		let dispatchParams = {
			search_query: requestParams.searchQuery ? requestParams.searchQuery : "",
			order_by:
				requestParams.sortField !== null
					? requestParams.sortField
					: "name",
			order_direction: requestParams.sortOrder
				? requestParams.sortOrder
				: "asc",
			page: requestParams.page + 1,
			size: requestParams.rows,
		};

		setQueryParams(dispatchParams);
		setRunGetListAPICall(true)
	}, []);

	const {
		isPending: isSubmittingAddRemoveHeaderColumns,
		mutate: submitAddRemoveHeaderColumns,
	} = useMutation({
		mutationFn: ReportsService.getWSIBAddRemoveHeaderColumns,
		onSuccess: (res: any | IServerError | IResponse) => {
			if (res.status === 200) {
				setRunGetListAPICall(true)
				return queryClient.invalidateQueries({
					queryKey: [WSIB_REPORT_QUERY_KEY],
				});
			} else {
				setSnackbarData({
					open: true,
					message: res.message
						? res.message
						: "Something went wrong, try again later!",
					severity: "error",
				});
			}
		},
		onError: (err: any) => {
			setSnackbarData({
				open: true,
				message: err.message,
				severity: "error",
			});
		},
	});

	useEffect(() => {
		if (submitAddRemoveColumnsHandler) {
			submitAddRemoveHeaderColumns({
				clientId: clientId,
				columns: submitAddRemoveColumnsHandler,
				navigateToLogout: navigateToLogout,
			});
		}
	}, [submitAddRemoveColumnsHandler]);


	const onSnackbarClosed = (
		event: React.SyntheticEvent | Event,
		reason?: string
	) => {
		if (reason === "clickaway") {
			return;
		}
		setSnackbarData(INITIAL_SNACKBAR_DATA);
	};

	useRemoveQueriesOnUnmount(["employee-report"]);

	const upadtedWSIBListItems = useMemo(() => {
		return (
			wsibReportData &&
			wsibReportData.items &&
			wsibReportData?.items.map((item: any) => ({
				...item,
			}))
		);
	}, [wsibReportData]);

	const expandedLabels = ["Reports", "Remittance"];
	const expandedWSIBOrSetupItem = "WSIB";
	const enhanceData = (data: any[], parentLabel?: string): any[] => {
		return data.map((item) => {
			let isExpanded = expandedLabels.includes(item.label);
			if (
				parentLabel === "Remittance" &&
				item.label === expandedWSIBOrSetupItem
			) {
				isExpanded = true;
			}
			const enhancedItem = {
				...item,
				expanded: item.items ? isExpanded : false,
				items: item.items ? enhanceData(item.items, item.label) : undefined,
				path: item.label === expandedWSIBOrSetupItem && "/reports/wsib",
			};
			return enhancedItem;
		});
	};


	// getting latest updated values of form fields using watch method
	const { watch } = formMethods;
	const values = watch([
		"selectedYear",
		"groupBy",
		"department",
		"startDate",
		"endDate",
	]);

	const savePdfAsFile = (xmlData: any, filename: any) => {
		const blob = new Blob([xmlData], { type: "pdf;charset=utf-8" });
		FileSaver.saveAs(blob, filename);
	};

	const onExportButtonClick = async (selectedOption: string) => {
		const [selectedYear, groupBy, department, startDate, endDate] = values;

		try {
			setIsLoading(true);
			const response = await ReportsService.exportWSIBOntarioReport(
				selectedOption,
				clientIdStore,
				// selectedYear,
				getDDLLabelByValue(yearsDDLItems, selectedYear),
				groupBy,
				department,
				formatDate(startDate),
				formatDate(endDate),
				queryParams
			);

			if (response) {
				if (selectedOption === "PDF") {
					if (response.status === 400) {
						setSnackbarData({
							open: true,
							message: response.data.message
								? response.data.message
								: "Something went wrong.",

							severity: "error",
						});
					} else {
						const filename = `${clientName} - WSIB-Report.pdf`;
						savePdfAsFile(response, filename);
					}
				} else if (selectedOption === "XLS") {
					if (response.status === 400) {
						setSnackbarData({
							open: true,
							message: response.data.message
								? response.data.message
								: "Something went wrong.",

							severity: "error",
						});
					} else {
						const filename = `${clientName} - WSIB-Report.xlsx`;
						savePdfAsFile(response, filename);
					}
				} else {
					const fileType =
						selectedOption === "XLS"
							? "application/vnd.ms-excel"
							: "text/csv;charset=utf-8;";
					const fileExtension = selectedOption === "XLS" ? "xls" : "csv";

					const blob = new Blob([response], { type: fileType });
					FileSaver.saveAs(blob, `${clientName} - WSIB-Report.${fileExtension}`);
				}
			} else {
				console.error("Error: No data received from the server");
			}
		} catch (err) {

			setIsLoading(false);
		} finally {
			setIsLoading(false);
		}

	};

	useEffect(() => {
		dispatch(
			setSelectedActivityLogActions({
				entityId1: "",
				entityId2: "",
				entityId3: "",
				screenName: EMPLOYEE_LIST_SCREEN_SLUG,
				accountantName: "",
				activityType: "activity_type=delete",
				activityLogVisibility: true,
			})
		);
		dispatch(setSelectedStickyNavBar({ isSticky: true }));

		if (drawerMenuOptions && drawerMenuOptions.length > 0) {
			const enhancedData = enhanceData(drawerMenuOptions);
			dispatch(setSelectedDrawerOptionsMenu({ menu: enhancedData }));
		}
		dispatch(setSelectedStickyNavBar({ isSticky: false }));
	}, [drawerMenuOptions]);

	const image =
		"https://taxslips.blob.core.windows.net/taxslips-public-images-container/empty_employee_list.png";

	return (
		<>
			<div
				style={{
					margin: "15px 0",
					fontSize: "1.5rem",
					fontWeight: "bold",
					fontFamily: "'Roboto','Helvetica','Arial',sans-serif",
				}}
			>
				{"WSIB Report"}
			</div>

			<FormProvider {...formMethods}>
				<form onSubmit={formMethods.handleSubmit(onSubmit)}>
					<WSIBReportHeader
						setStateRunGetListAPICall={(value: boolean) => setRunGetListAPICall(value)}
						departmentsDDL={departmentsDDLItems}
						yearDDLItems={yearsDDLItems}
						taxYear={taxYear}
					/>
				</form>
			</FormProvider>

			<Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
				<BackdropCircularProgress
					open={
						isPending ||
						isSubmittingAddRemoveHeaderColumns ||
						isLoading ||
						isYearTODateDDLLoading ||
						isDepartmentLoadingDDL
					}
					testId="wsib-report"
				/>


				<WSIBReportDataGridWithColumnToggled
					columns={
						wsibReportData?.headerColumns
							? wsibReportData.headerColumns.filter(
								(item: any) => item.field !== "id"
							)
							: []
						// wsibReportData?.headerColumns ?? []
					}
					completeHeaderColumnsList={
						wsibReportData
							&& wsibReportData
								?.totalHeaderColumns &&
							Object.keys(wsibReportData.totalHeaderColumns).length !== 0
							? wsibReportData.totalHeaderColumns
							: {}
						// wsibReportData ?? []
					}
					submitAddRemoveColumnsHandler={(value: any) =>
						setSubmitAddRemoveColumnsHandler(value)
					}
					onColumnReorder={(value: any) => setOnColumnReorder(value)}
					rows={
						wsibReportData && upadtedWSIBListItems
							? upadtedWSIBListItems
							: []
						// wsibReportData?.items ?? []
						// itams
					}
					showIndexColumn={true}
					showChecboxColumn={false}
					showSettingColumn={false}
					onSnackbarMessageData={(value: any) => setSnackbarData(value)}
					totalRecords={
						wsibReportData && wsibReportData.total
							? wsibReportData.total
							: 0
					}
					initialPageSize={INITIAL_PAGE_SIZE}
					rowsPerPageOptions={ROWS_PER_PAGE_PER_OPTIONS}
					onRequestParamsChange={onRequestParamsChange}
					searchFieldPlaceholder={"Search..."}
					changeLoginForGetEmptyItemsBySearchQuery={(value: any) =>
						setzeroEmployee(value)
					}
					pageTitle={"WSIB Report"}
					showRowExpandedView={true}
					onExportButtonClick={onExportButtonClick}
				/>

				{wsibReportData ? (
					<AppBar
						position="sticky"
						color="default"
						elevation={1}
						sx={{
							backgroundColor: "#F7F7F7",

							boxShadow: "none",
							alignItems: "center",
							zIndex: 0,
							position: "sticky",
							bottom: 0,
						}}
					></AppBar>
				) : (
					<></>
				)}
			</Grid>

			<EnhancedSnackbar
				message={snackbarData.message}
				onCloseHandler={onSnackbarClosed}
				severity={snackbarData.severity}
				testId={`snackbar`}
				open={snackbarData.open}
			/>
		</>
	);
};
